import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Select,
    MenuItem,
    FormControl,
    Typography,
    AppBar,
    Toolbar,
    IconButton,
    TextField,
    InputLabel,
    Grid,
    Switch,
} from '@material-ui/core';
import {
    FilterList as FilterIcon,
    Edit as EditIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Pagination } from '@material-ui/lab';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getSellerLocations, updateSellerLocation } from '../../../store/sellerLocation/sellerLocationActions';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
    },
    appBar: {
        backgroundColor: 'white',
        color: 'black',
        boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2)',
    },
    toolbar: {
        justifyContent: 'flex-end',
    },
    createButton: {
        backgroundColor: '#3f51b5',
        color: 'white',
        marginRight: theme.spacing(2),
        '&:hover': {
            backgroundColor: '#34495e',
        },
    },
    content: {
        padding: theme.spacing(2),
    },
    controls: {
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
    },
    controlsLeft: {
        display: 'flex',
        alignItems: 'center',
        '& > *': {
            marginRight: theme.spacing(2),
        },
    },
    filterButton: {
        backgroundColor: '#3f51b5',
        color: 'white',
        '&:hover': {
            backgroundColor: '#34495e',
        },
    },
    tableContainer: {
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14)',
        borderRadius: theme.spacing(1),
    },
    sellerIdCell: {
        color: theme.palette.primary.main,
        fontWeight: 500,
    },
    headerCell: {
        backgroundColor: '#e0e0e0',
        fontWeight: 600,
    },
    statusBadge: {
        display: 'flex',
        alignItems: 'center',
        '& > *:first-child': {
            marginRight: theme.spacing(1),
        },
    },
    statusDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
    },
    statusText: {
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.primary.main,
    },
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(3),
    },
    editButton: {
        color: '#3f51b5',
        '&:hover': {
            backgroundColor: 'rgba(63, 81, 181, 0.04)',
        },
    },
    editButtonText: {
        marginLeft: theme.spacing(0.5),
        fontSize: '13px',
        fontWeight: 500,
    },
    formControl: {
        minWidth: 120,
    },
    filterField: {
        marginBottom: theme.spacing(2),
        width: '100%',
    },
    clearButton: {
        marginRight: theme.spacing(1),
        color: '#666',
        borderColor: '#666',
    },
    applyButton: {
        backgroundColor: '#3f51b5',
        color: 'white',
        '&:hover': {
            backgroundColor: '#34495e',
        },
    },
    activeFilterBadge: {
        backgroundColor: '#f44336',
        color: 'white',
        borderRadius: '50%',
        width: 20,
        height: 20,
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: theme.spacing(0.5),
    },
    statusSwitch: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
    },
    statusLabel: {
        fontSize: '12px',
        color: '#666',
        marginTop: theme.spacing(0.5),
    },
}));

export default function SellerLocation() {
    const classes = useStyles();
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [updatingStatus, setUpdatingStatus] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        warehouse_id: '',
        seller_name: '',
        fulfillment_type: '',
    });

    const history = useHistory();
    const dispatch = useDispatch();

    // For demo, replace with your data source
    const sellerLocations = useSelector(state => state.sellerLocation.sellerLocations) || [];

    const handleCreateSellerLocation = () => {
        history.push("/location/CreateSellerLocation");
    };

    const handleEditSeller = (seller) => {
         history.push({
            pathname: "/location/CreateSellerLocation",
            state: {
                editMode: true,
                sellerLocationData: seller
            }
        });
    };

    const handleStatusToggle = (seller, newStatus) => {
        setUpdatingStatus(seller._id);
        
        const { _id, __v, createdAt, updatedAt, ...restData } = seller;
        const updateData = {
            ...restData,
            status: newStatus
        };

        dispatch(updateSellerLocation(
            seller._id, 
            updateData,
            () => {
                setUpdatingStatus(null);
                dispatch(getSellerLocations());
            }
        ));
    };

    // Filter functions
    const handleFilterOpen = () => {
        setFilterOpen(true);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const applyFilters = () => {
        let filtered = sellerLocations;

        if (filters.warehouse_id) {
            filtered = filtered.filter(item =>
                item.warehouse_id?.toLowerCase().includes(filters.warehouse_id.toLowerCase())
            );
        }
        if (filters.seller_name) {
            filtered = filtered.filter(item => item.seller_name?.toString()?.toLowerCase().includes(filters.seller_name.toLowerCase()))
        }
        if (filters.fulfillment_type) {
            filtered = filtered.filter(item =>
                item.fulfillment_type?.toLowerCase().includes(filters.fulfillment_type.toLowerCase())
            );
        }

        setFilteredData(filtered);
        setCurrentPage(1);
        setFilterOpen(false);
    };

    const clearFilters = () => {
        setFilters({
            warehouse_id: '',
            seller_name: '',
            fulfillment_type: '',
        });
        setFilteredData(sellerLocations);
        setCurrentPage(1);
        setFilterOpen(false);
    };

    // Check if any filter is active
    const hasActiveFilters = Object.values(filters).some(value => value !== '');
    const activeFilterCount = Object.values(filters).filter(value => value !== '').length;

    // Get unique values for dropdown options
    const getUniqueValues = (field) => {
        return [...new Set(sellerLocations.map(item => item[field]).filter(Boolean))];
    };

    // Pagination logic
    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    useEffect(() => {
        dispatch(getSellerLocations());
    }, [dispatch]);

    useEffect(() => {
        setFilteredData(sellerLocations);
    }, [sellerLocations]);

    return (
        <div className={classes.root}>
            <AppBar position="static" className={classes.appBar}>
                <Toolbar className={classes.toolbar}>
                    <Button
                        variant="contained"
                        onClick={handleCreateSellerLocation}
                        className={classes.createButton}
                    >
                        Create Seller Location
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth={false} className={classes.content}>
                <Box className={classes.controls}>
                    <Box className={classes.controlsLeft}>
                        <Button
                            variant="contained"
                            startIcon={<FilterIcon />}
                            className={classes.filterButton}
                            onClick={handleFilterOpen}
                        >
                            Filter
                            {hasActiveFilters && (
                                <span className={classes.activeFilterBadge}>
                                    {activeFilterCount}
                                </span>
                            )}
                        </Button>
                        <Typography variant="body2" color="textSecondary">
                            {startIndex + 1} - {Math.min(endIndex, totalItems)} of {totalItems}
                        </Typography>
                        <FormControl className={classes.formControl}>
                            <Select
                                value={rowsPerPage}
                                onChange={(e) => setRowsPerPage(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value={10}>10 Per Page</MenuItem>
                                <MenuItem value={25}>25 Per Page</MenuItem>
                                <MenuItem value={50}>50 Per Page</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {filterOpen && (
                    <Box>
                        <Grid container spacing={2}>
                              <Grid item xs={12} sm={6} lg={3}>
                            <TextField
                                label="Warehouse ID"
                                value={filters.warehouse_id}
                                onChange={(e) => handleFilterChange('warehouse_id', e.target.value)}
                                className={classes.filterField}
                                variant="outlined"
                                size="small"
                                placeholder="Search by Warehouse ID..."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <TextField
                                label="Seller Name"
                                value={filters.seller_name}
                                onChange={(e) => handleFilterChange('seller_name', e.target.value)}
                                className={classes.filterField}
                                variant="outlined"
                                size="small"
                                placeholder="Search by seller name..."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <FormControl className={classes.filterField} variant="outlined" size="small">
                                <InputLabel>Fulfillment Type</InputLabel>
                                <Select
                                    value={filters.fulfillment_type}
                                    onChange={(e) => handleFilterChange('fulfillment_type', e.target.value)}
                                    label="Fulfillment Type"
                                >
                                    <MenuItem value="">All Types</MenuItem>
                                    {getUniqueValues('fulfillment_type').map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <Button
                                    onClick={clearFilters}
                                    variant="outlined"
                                    className={classes.clearButton}
                                >
                                    Clear All
                                </Button>
                                <Button
                                    onClick={applyFilters}
                                    variant="contained"
                                    className={classes.applyButton}
                                >
                                    Apply Filters
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headerCell}>Warehouse ID</TableCell>
                                <TableCell className={classes.headerCell}>Seller Name</TableCell>
                                <TableCell className={classes.headerCell}>Country</TableCell>
                                <TableCell className={classes.headerCell}>Fulfillment Type</TableCell>
                                <TableCell className={classes.headerCell}>Warehouse Name</TableCell>
                                <TableCell className={classes.headerCell}>State</TableCell>
                                <TableCell className={classes.headerCell}>Area</TableCell>
                                <TableCell className={classes.headerCell}>Status</TableCell>
                                <TableCell className={classes.headerCell}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.map((seller, index) => (
                                <TableRow
                                    key={seller._id}
                                    hover
                                    style={{
                                        backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9',
                                    }}
                                >
                                    <TableCell>{seller.warehouse_id}</TableCell>
                                    <TableCell className={classes.sellerIdCell}>
                                        {seller.seller_name}
                                    </TableCell>
                                    <TableCell>{seller.country}</TableCell>
                                    <TableCell>{seller.fulfillment_type}</TableCell>
                                    <TableCell>{seller.warehouse_name}</TableCell>
                                    <TableCell>{seller.warehouse_state}</TableCell>
                                    <TableCell>{seller.area}</TableCell>
                                    <TableCell>
                                        <div className={classes.statusSwitch}>
                                            <Switch
                                                checked={seller.status === true || seller.status === 'active'}
                                                onChange={(e) => handleStatusToggle(seller, e.target.checked)}
                                                color="primary"
                                                disabled={updatingStatus === seller._id}
                                                size="small"
                                            />
                                            <Typography className={classes.statusLabel}>
                                                {updatingStatus === seller._id 
                                                    ? 'Updating...' 
                                                    : (seller.status === true || seller.status === 'active') 
                                                        ? 'Active' 
                                                        : 'Inactive'
                                                }
                                            </Typography>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            className={classes.editButton}
                                            size="small"
                                            onClick={() => handleEditSeller(seller)}
                                        >
                                            <EditIcon /> <span className={classes.editButtonText}>Edit</span>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box className={classes.pagination}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(event, value) => setCurrentPage(value)}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            </Container>

        </div>
    );
}